type WindowErrorDetails = {
  message: unknown;
  source?: string | null;
  error?: unknown;
};

type StartupStorage = Pick<Storage, 'getItem' | 'setItem'>;

const DYNAMIC_IMPORT_RELOAD_KEY = 'rudi_dynamic_import_reloads';
const STARTUP_RELOAD_ATTEMPT_KEY = 'rudi_startup_reload_attempts';
const DEVELOPMENT_RELOAD_KEY = 'rudi_dev_route_reload';
const DYNAMIC_IMPORT_RELOAD_WINDOW_MS = 60_000;
const DYNAMIC_IMPORT_RELOAD_LIMIT = 3;

export function isSameOriginApplicationResource(
  target: EventTarget | null,
  pageUrl: string = window.location.href
): boolean {
  const element = target as Element | null;
  if (!element) return false;

  const tag = element.tagName;
  const resourceUrl =
    tag === 'SCRIPT'
      ? element.getAttribute('src')
      : tag === 'LINK'
        ? element.getAttribute('href')
        : null;
  if (!resourceUrl) return false;

  try {
    const page = new URL(pageUrl);
    return new URL(resourceUrl, page).origin === page.origin;
  } catch {
    return false;
  }
}

export function isDynamicImportError(error: unknown): boolean {
  const message = String(error instanceof Error ? error.message : (error ?? ''));
  return [
    'Importing a module script failed',
    'Failed to fetch dynamically imported module',
    'error loading dynamically imported module',
    'Failed to load module script',
    'Failed to fetch',
    'NetworkError when attempting to fetch resource',
    'Load failed',
  ].some(signature => message.includes(signature));
}

export function claimDynamicImportReload(
  error: unknown,
  storage: StartupStorage = sessionStorage,
  now: number = Date.now()
): boolean {
  if (!isDynamicImportError(error)) return false;
  try {
    const stored = JSON.parse(storage.getItem(DYNAMIC_IMPORT_RELOAD_KEY) ?? 'null') as {
      startedAt?: unknown;
      count?: unknown;
    } | null;
    const withinWindow =
      typeof stored?.startedAt === 'number' &&
      now - stored.startedAt < DYNAMIC_IMPORT_RELOAD_WINDOW_MS;
    const startedAt = withinWindow ? stored.startedAt : now;
    const count = withinWindow && typeof stored?.count === 'number' ? stored.count : 0;
    if (count >= DYNAMIC_IMPORT_RELOAD_LIMIT) return false;
    storage.setItem(DYNAMIC_IMPORT_RELOAD_KEY, JSON.stringify({ startedAt, count: count + 1 }));
    return true;
  } catch {
    return false;
  }
}

export function clearDynamicImportReloads(
  storage: Pick<Storage, 'removeItem'> = sessionStorage
): void {
  try {
    storage.removeItem(DYNAMIC_IMPORT_RELOAD_KEY);
  } catch {
    // Storage can be unavailable in restricted browser contexts.
  }
}

export function clearStartupReloadAttempts(
  storage: Pick<Storage, 'removeItem'> = sessionStorage
): void {
  try {
    storage.removeItem(STARTUP_RELOAD_ATTEMPT_KEY);
    storage.removeItem(DEVELOPMENT_RELOAD_KEY);
  } catch {
    // Storage can be unavailable in restricted browser contexts.
  }
}

export function isMaskedScriptError(details: WindowErrorDetails): boolean {
  return details.error == null && String(details.message ?? '') === 'Script error.';
}

export function shouldShowStartupFallbackForWindowError(details: WindowErrorDetails): boolean {
  if (isMaskedScriptError(details)) {
    return false;
  }

  const source = details.source ?? '';
  if (source.includes('@vite/client') || source.includes('node_modules/vite')) {
    return false;
  }

  return true;
}
